import React from 'react';

import {
  DailyRewardSlotDto,
  DailyRewardStateDto,
  EDailyRewardSlotStatus,
  EDailyRewardType,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import {
  ChestRewardContent,
  resolveChestSlotReward,
} from './ChestRewardContent';
import { ChestVisual } from './ChestVisual';
import styles from './Chests.module.scss';
import type { IChestOpeningState } from './useChestOpening';

interface IChestSlotProps {
  slot: DailyRewardSlotDto;
  rewardState?: DailyRewardStateDto;
  currentDayIndex?: number;
  openingState: IChestOpeningState;
  isMutationPending: boolean;
  isSlotTransitionActive: (dayIndex: number) => boolean;
  onOpenChest: (dayIndex: number) => void;
  onShowAlreadyOpenedModal: () => void;
  onShowNotPlayedModal: () => void;
}

export const ChestSlot: React.FC<IChestSlotProps> = ({
  currentDayIndex,
  isMutationPending,
  isSlotTransitionActive,
  onOpenChest,
  onShowAlreadyOpenedModal,
  onShowNotPlayedModal,
  openingState,
  rewardState,
  slot,
}) => {
  const { t } = useTranslation('dashboard');
  const {
    justOpenedDayIndex,
    openingDayIndex,
    openingReward,
    pendingOpenDayIndex,
  } = openingState;

  const isAvailable = slot.status === EDailyRewardSlotStatus.available;
  const isOpened = slot.status === EDailyRewardSlotStatus.opened;
  const isLocked = slot.status === EDailyRewardSlotStatus.locked;
  const isCurrentDay = currentDayIndex === slot.dayIndex;

  const rewardOverride =
    openingReward?.dayIndex === slot.dayIndex ? openingReward : null;
  const isPendingOpen = pendingOpenDayIndex === slot.dayIndex;
  const isOpening = openingDayIndex === slot.dayIndex;
  const resolvedReward = resolveChestSlotReward(slot, openingReward);
  const { rewardType } = resolvedReward;
  const hasRewardBackground =
    rewardType !== undefined && rewardType !== EDailyRewardType.empty;
  const rewardAreaClassName = cx(
    styles.rewardArea,
    hasRewardBackground && styles.rewardAreaBackground,
    isOpening && styles.rewardAreaDuringOpening,
  );
  const isJustOpened = justOpenedDayIndex === slot.dayIndex;
  const isLocalTransitionActive = isSlotTransitionActive(slot.dayIndex);
  const showRewardLayout = isOpening || isOpened || Boolean(rewardOverride);
  const dayLabel = t('chests.dayLabel', { day: slot.dayIndex });

  const handleClick = () => {
    if (isMutationPending || isLocalTransitionActive) return;

    if (rewardState?.openedToday) {
      onShowAlreadyOpenedModal();
      return;
    }

    if (rewardState && !rewardState.canOpen) {
      onShowNotPlayedModal();
      return;
    }

    if (!isCurrentDay || !isAvailable) return;

    onOpenChest(slot.dayIndex);
  };

  return (
    <div
      data-day-index={slot.dayIndex}
      data-testid="chest-slot"
      style={{ display: 'contents' }}
    >
      <button
        className={cx(
          styles.chestButton,
          isAvailable && !showRewardLayout && styles.chestButtonAvailable,
          isLocked && styles.chestButtonLocked,
          showRewardLayout && styles.chestButtonOpened,
          isJustOpened && styles.chestButtonJustOpened,
          isOpening && styles.chestButtonOpening,
        )}
        data-testid={isCurrentDay ? 'chest-current-day' : undefined}
        disabled={isMutationPending || isLocalTransitionActive}
        onClick={handleClick}
        type="button"
      >
        {showRewardLayout ? (
          <>
            <span className={styles.dayLabel}>{dayLabel}</span>
            <span aria-hidden="true" className={styles.divider} />
            <div className={styles.rewardStage}>
              <div
                className={rewardAreaClassName}
                data-testid={isCurrentDay ? 'chest-reward' : undefined}
              >
                <ChestRewardContent {...resolvedReward} />
              </div>
              {isOpening ? (
                <ChestVisual isExplosionOverlay variant="opening" />
              ) : null}
            </div>
          </>
        ) : (
          <ChestVisual
            dayLabel={dayLabel}
            isPendingOpen={isPendingOpen}
            variant="idle"
          />
        )}
      </button>
    </div>
  );
};
